import React from 'react';
import { useRouter } from 'next/router';
import { StepLayout } from '../Step/StepLayout';
import { HeaderNav } from './HeaderNav';
import { useOnboarder } from './OnboarderContext';
import { UseRawUrlDataIfApplicable } from './UseRawUrlDataIfApplicable';
import { withPageWrapper } from './withPageWrapper';
import type { Role } from '@99designs/site-navigation/consts';
import type { Step } from './Config';
import type { WithPageWrapperProps } from './withPageWrapper';

interface RouterProps extends WithPageWrapperProps {
    role: Role;
}

function hasRequiredDataForStep(stepIndex: number, steps: Step<unknown>[], data: unknown): boolean {
    return steps
        .slice(0, stepIndex)
        .map((step) => step.isSkippable || step.canGoToNextStep(data))
        .reduce((prev, current) => prev && current, true);
}

/**
 * Router contains all the routes in an onboarder
 * The routes are rendered based on the config
 */
export function BaseRouter({ role }: RouterProps) {
    const router = useRouter();
    const { steps, config, data, saveData } = useOnboarder();
    const basePath = config.basePath;
    const [step, name] = router.query?.params || [];

    if (!step) {
        return (
            <>
                <HeaderNav userRole={role} />
                <config.Launcher />
            </>
        );
    }

    if (step === 'recommendation') {
        return (
            <UseRawUrlDataIfApplicable>
                <config.Recommendation />
            </UseRawUrlDataIfApplicable>
        );
    }

    const stepIndex = parseInt(step, 10) - 1;
    const currentStep = steps[stepIndex];

    if (!currentStep || !hasRequiredDataForStep(stepIndex, steps, data)) {
        router.replace(basePath);
        return null;
    }

    if (currentStep.key !== name) {
        router.replace(`${basePath}/${step}/${currentStep.key}`);
        return null;
    }

    return (
        <>
            <HeaderNav userRole={role} />
            <StepLayout>
                <currentStep.Component data={data} onChange={saveData} />
            </StepLayout>
        </>
    );
}

export const Router = withPageWrapper(BaseRouter);
